/**
 * Hydrates the member home (logged-in users). Three blocks were formerly
 * hardcoded and are now filled from real APIs:
 *
 *   1. Hero resume chip             - shared /api/topics/recent?limit=20
 *   2. Up next (3 cards)            - /api/topics/recommended?limit=3
 *   3. Three paid-hero metrics      - /api/user/learning-stats
 *
 * Additionally, the "Up next" area is now a 5-second auto-rotating
 * carousel alternating between the 3 recommended cards and a 3D keyword
 * cloud for the topic the user is currently working on (the resume
 * topic if there is one, otherwise the first recommendation). The
 * keyword cloud reuses renderKeywordCloud from the topic detail page.
 *
 * The /api/user/learning-stats endpoint returns real COUNT-based
 * metrics: topicsExplored, lessonsCompleted, daysActive.
 */
import { byId, awaitMinSkeletonDelay } from "./domUtils";
import type { Root } from "./prototypeTypes";
import type { PrototypeApiClient } from "./apiClient";
import { getHomeRecommendedTopics } from "./homeRecommendedTopics";
import { HOME_LIBRARY_ITEM_LIMIT, type HomeRecentTopicsResponse } from './homeRecentHydrator';
import { renderKeywordCloud } from "./keywordCloud";
import type { KeywordItem } from "./keywordCloud";
import { coverUrlForTopic, categoriesFromClassification, DEFAULT_COVER_URL } from "../../domain/topicCover";

interface UpNextTopic {
	slug: string;
	title: string;
	subtitle?: string;
	description?: string;
	classification?: import('../../domain/topicClassification').TopicClassification | null;
	category?: string;
	chapterCount?: number;
	audioMinutes?: number;
	coverIcon?: string | null;
	coverGradient?: string | null;
	coverImageUrl?: string;
}

let hydrationGeneration = 0;

/** Invalidates hidden member-workspace paints after leaving Home. */
export function deactivateHomeMember(): void {
	hydrationGeneration += 1;
}

interface HomeMemberHydration {
	layout: Promise<void>;
	enhancements: Promise<void>;
}

/**
 * Hydrates only the member Hero structure that determines Home's return
 * anchor: resume copy/actions and the Up next rows. Keyword rendering and
 * learning metrics are visual enrichment and deliberately do not gate it.
 */
export function hydrateHomeMemberLayout(root: Root, api: PrototypeApiClient, recentRequest?: Promise<HomeRecentTopicsResponse>): Promise<void> {
	const hydration = startHomeMemberHydration(root, api, recentRequest);
	// Enrichment retains the same generation guard, so it cannot paint after
	// Home leaves even though a Back snapshot may already be valid.
	void hydration.enhancements.catch(() => undefined);
	return hydration.layout;
}

/** Full member hydration remains available to callers/tests that need enrichment settled. */
export async function hydrateHomeMember(root: Root, api: PrototypeApiClient): Promise<void> {
	const hydration = startHomeMemberHydration(root, api);
	await hydration.layout;
	await hydration.enhancements;
}

function startHomeMemberHydration(root: Root, api: PrototypeApiClient, recentRequest?: Promise<HomeRecentTopicsResponse>): HomeMemberHydration {
	const generation = ++hydrationGeneration;
	const isCurrent = () => generation === hydrationGeneration;
	// Fetch the shared Recent snapshot + recommended topics in parallel so we
	// can determine the "current topic" (resumable Recent entry if present,
	// else first recommendation) and feed its keywords to the cloud slide.
	// The Recently Opened rail consumes the same in-flight Recent response; do
	// not issue a second progress endpoint request just to paint the Hero.
	const inputs = Promise.allSettled([
		recentRequest ?? api.topicRecent({ limit: HOME_LIBRARY_ITEM_LIMIT }),
		getHomeRecommendedTopics(api),
	]).then(([recentResp, recommended]) => {
		const resume = recentResp.status === 'fulfilled'
			? (recentResp.value.topics ?? []).flatMap((topic) => {
				const progress = topic.latestProgress;
				if (!progress) return [];
				// The watermarked percent stays pinned at 100 forever, so the
				// hero's ring tells the truth from the dedicated resume position
				// when the row has one — mirroring the Recent card.
				const displayPercent = typeof progress.mediaResumePositionSeconds === 'number'
					&& typeof progress.audioDurationSeconds === 'number' && progress.audioDurationSeconds > 0
					? Math.min(100, Math.max(0, Math.round((progress.mediaResumePositionSeconds / progress.audioDurationSeconds) * 100)))
					: progress.progressPercent;
				return [{
					topicSlug: topic.slug,
					topicTitle: topic.title,
					topicSubtitle: topic.subtitle,
					chapterIndex: progress.chapterIndex,
					chapterTitle: progress.chapterTitle,
					status: progress.status,
					progressPercent: displayPercent,
				}];
			})[0] ?? null
			: null;
		const items = recommended.status === 'fulfilled'
			? (recommended.value.topics as UpNextTopic[]).slice(0, 3)
			: [];
		const currentSlug = resume?.topicSlug ?? items[0]?.slug ?? null;
		return { resume, items, currentSlug, currentTopic: currentSlug ? items.find((topic) => topic.slug === currentSlug) ?? null : null };
	});

	const layout = inputs.then(async ({ resume, items, currentSlug }) => {
		if (!isCurrent()) return;
		await Promise.allSettled([
			hydrateResumeChip(root, resume, items, currentSlug, isCurrent),
			hydrateUpNext(root, items, isCurrent),
		]);
	});
	const enhancements = inputs.then(async ({ currentSlug, currentTopic }) => {
		if (!isCurrent()) return;
		await Promise.allSettled([
			hydrateKeywordCloud(root, api, currentSlug, currentTopic?.classification ?? null, currentTopic?.coverImageUrl, isCurrent),
			hydrateMetrics(root, api, isCurrent),
		]);
	});
	return { layout, enhancements };
}

/**
 * Re-paint the hero resume chip from a fresh Recent response without the full
 * member hydration (recommendation/keyword-cloud machinery untouched). The
 * Recent write-path reconcile calls this so returning to Home — or simply
 * listening — keeps the hero on the same story as the rail.
 */
export function refreshHeroResumeFromRecent(root: Root, recent: HomeRecentTopicsResponse): void {
	const entry = (recent.topics ?? [])[0];
	const progress = entry?.latestProgress;
	if (!progress) return;
	const displayPercent = typeof progress.mediaResumePositionSeconds === 'number'
		&& typeof progress.audioDurationSeconds === 'number' && progress.audioDurationSeconds > 0
		? Math.min(100, Math.max(0, Math.round((progress.mediaResumePositionSeconds / progress.audioDurationSeconds) * 100)))
		: progress.progressPercent;
	void hydrateResumeChip(root, {
		topicTitle: entry.title,
		topicSubtitle: entry.subtitle,
		chapterIndex: progress.chapterIndex,
		chapterTitle: progress.chapterTitle,
		progressPercent: displayPercent,
	}, [], entry.slug, () => true);
}

async function hydrateUpNext(root: Root, items: UpNextTopic[], isCurrent: () => boolean): Promise<void> {
	const container = byId(root, "paidHeroUpNext");
	if (!container) return;
	await awaitMinSkeletonDelay();
	if (!isCurrent()) return;
	if (items.length === 0) {
		container.replaceChildren(buildEmpty("No suggestions yet."));
		return;
	}
	container.replaceChildren(...items.map(buildUpNextCard));
}

async function hydrateResumeChip(
	root: Root,
	resume: {
		topicTitle: string;
		topicSubtitle?: string;
		chapterIndex: number;
		chapterTitle?: string;
		progressPercent: number;
	} | null,
	items: UpNextTopic[],
	currentSlug: string | null,
	isCurrent: () => boolean,
): Promise<void> {
  const titleEl = byId(root, "paidHeroTitle");
  const topicDescEl = byId(root, "paidHeroTopicDesc");
  const subtitleEl = byId(root, "paidHeroSubtitle");
  const primaryAction = byId(root, "paidHeroPrimaryAction");
  const browseAction = byId(root, "paidHeroBrowseAction");
	if (!titleEl) return;
	await awaitMinSkeletonDelay();
	if (!isCurrent()) return;
	// This secondary CTA is the member-wide AI Create entrypoint in every
	// Hero state. Reapply both fields before branching so a retained/reused DOM
	// cannot keep a prior viewer's Browse action.
	if (browseAction) {
		browseAction.textContent = 'AI Create';
		browseAction.dataset.protoClick = "showView('ai-create');setActiveTab('ai-create')";
	}

	// Prefer the subtitle from the exact resume record. Recommendation data is
	// only a fallback because the selected recent item may differ from it.
	const matchedTopic = currentSlug ? items.find((t) => t.slug === currentSlug) : null;
	const topicDesc = resume?.topicSubtitle || matchedTopic?.subtitle || matchedTopic?.description || "";

	if (!resume) {
		// No learning progress yet. The keyword cloud already keys off the
		// first recommendation, so the hero selects that same topic instead
		// of an empty "choose a topic" state that visually contradicted the
		// cloud showing its keywords.
		const first = items[0];
		if (first) {
			titleEl.textContent = first.title;
			if (topicDescEl) topicDescEl.textContent = first.subtitle || first.description || "";
			if (subtitleEl) {
				const chap = typeof first.chapterCount === "number" ? first.chapterCount : "-";
				const min = typeof first.audioMinutes === "number" ? first.audioMinutes : "-";
				subtitleEl.textContent = `${chap} chapters · ${min} min`;
			}
			if (primaryAction) {
				primaryAction.textContent = "Start exploring";
				primaryAction.dataset.protoClick = `showTopicDetail('${first.slug}')`;
			}
			if (browseAction) browseAction.style.display = "";
			return;
		}
		// No recommendations at all — fall back to the browsing CTA.
		titleEl.textContent = "Your learning desk";
    if (topicDescEl) topicDescEl.textContent = "";
    if (subtitleEl) subtitleEl.textContent = "Choose a topic to begin.";
    if (primaryAction) {
      primaryAction.textContent = "Browse all topics";
      primaryAction.dataset.protoClick = "navigateToBrowse('all')";
    }
    if (browseAction) browseAction.style.display = "none";
		return;
	}
	const r = resume;
	// Title becomes the topic title.
	titleEl.textContent = r.topicTitle;
	// Topic description goes between the title and the actions.
	if (topicDescEl) topicDescEl.textContent = topicDesc;
  // Chapter context now lives below the action buttons.
  if (subtitleEl) {
		const parts = [`Ch.${r.chapterIndex}`];
		if (r.chapterTitle) parts.push(r.chapterTitle);
		parts.push(`${r.progressPercent}% complete`);
    subtitleEl.textContent = parts.join(" · ");
  }
  if (primaryAction) {
    primaryAction.textContent = `Continue Ch.${r.chapterIndex}`;
    primaryAction.dataset.protoClick = "resumeListening()";
  }
  if (browseAction) browseAction.style.display = "";
}

async function hydrateKeywordCloud(
	root: Root,
	api: PrototypeApiClient,
	slug: string | null,
	classification: import('../../domain/topicClassification').TopicClassification | null,
	coverImageUrl?: string,
	isCurrent: () => boolean = () => true,
): Promise<void> {
	if (!isCurrent()) return;
	const cloudContainer = root.querySelector<HTMLElement>(".upnext-cloud");
	const cloudSlide = byId(root, "paidHeroCloud");
	// Set the cloud slide background to the current topic's category cover.
	if (cloudSlide) {
		cloudSlide.style.backgroundImage = `url("${coverImageUrl ?? coverUrlForTopic(categoriesFromClassification(classification))}")`;
	}
	if (!cloudContainer || !slug) {
		setCarouselEnabled(root, false);
		return;
	}
	try {
		const resp = await api.topicKeywords(slug);
		if (!isCurrent()) return;
		const keywords: KeywordItem[] = resp.keywords ?? [];
		if (keywords.length === 0) {
			setCarouselEnabled(root, false);
			return;
		}
		renderKeywordCloud(cloudContainer, keywords);
		// Start the carousel once the cloud is rendered.
		startCarousel(root);
	} catch {
		if (isCurrent()) setCarouselEnabled(root, false);
	}
}

async function hydrateMetrics(root: Root, api: PrototypeApiClient, isCurrent: () => boolean): Promise<void> {
	try {
		const stats = await api.getLearningStats();
		await awaitMinSkeletonDelay();
		if (!isCurrent()) return;
		const doc = root instanceof Document ? root : root.ownerDocument ?? document;
		const topicsEl = doc.querySelector<HTMLElement>('[data-bind="home.metricTopicsExplored"]');
		const chaptersEl = doc.querySelector<HTMLElement>('[data-bind="home.metricChaptersDone"]');
		const daysEl = doc.querySelector<HTMLElement>('[data-bind="home.metricDaysActive"]');
		if (topicsEl) topicsEl.textContent = String(stats.topicsExplored);
		if (chaptersEl) chaptersEl.textContent = String(stats.lessonsCompleted);
		if (daysEl) daysEl.textContent = String(stats.daysActive);
	} catch {
		// leave skeleton defaults if the endpoint fails
	}
}

function buildUpNextCard(topic: UpNextTopic): HTMLElement {
	const row = document.createElement("div");
	row.className = "paid-feature-row";
	row.tabIndex = 0;
	row.setAttribute('role', 'link');
	row.setAttribute('aria-label', `Open topic: ${topic.title}`);
	row.dataset.keyboardAction = 'true';
	row.setAttribute("data-proto-click", `showTopicDetail('${topic.slug}')`);
	const thumb = document.createElement("div");
	const hasIcon = !!(topic.coverIcon && topic.coverIcon.trim());
	thumb.className = `paid-feature-thumb ${topic.coverGradient || "grad-rag"}${hasIcon ? "" : " paid-feature-thumb--default"}`;
	thumb.style.backgroundImage = `url("${topic.coverImageUrl ?? coverUrlForTopic(categoriesFromClassification(topic.classification))}")`;
	row.appendChild(thumb);
	const wrap = document.createElement("div");
	const title = document.createElement("strong");
	title.textContent = topic.title;
	const meta = document.createElement("span");
	// Topic cards return `chapterCount` and `audioMinutes`; fall back
	// to em-dash so we never show literal "?" on the home hero for a
	// topic that simply hasn't published its counts yet.
	const chap = typeof topic.chapterCount === "number" ? topic.chapterCount : "-";
	const min = typeof topic.audioMinutes === "number" ? topic.audioMinutes : "-";
	meta.textContent = `${chap} chapters · ${min} min`;
	wrap.appendChild(title);
	wrap.appendChild(meta);
	row.appendChild(wrap);
	return row;
}

function buildEmpty(message: string): HTMLElement {
	const empty = document.createElement("div");
	empty.className = "loading-placeholder empty-state-inline";
	const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
	icon.setAttribute('aria-hidden', 'true');
	icon.setAttribute('viewBox', '0 0 24 24');
	icon.setAttribute('width', '28');
	icon.setAttribute('height', '28');
	icon.setAttribute('fill', 'none');
	icon.setAttribute('stroke', 'currentColor');
	icon.setAttribute('stroke-width', '1.5');
	icon.innerHTML = '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>';
	const label = document.createElement('span');
	label.textContent = message;
	empty.appendChild(icon);
	empty.appendChild(label);
	return empty;
}

/**
 * Up Next carousel: auto-rotates every 5s between the "Up next" cards
 * slide and the "Keyword cloud" slide. Only activates when both slides
 * have content. Left/right arrows + dot indicators support manual
 * navigation (which resets the timer). Pauses when the tab is hidden.
 */
const CAROUSEL_INTERVAL_MS = 5000;
const CAROUSEL_SWIPE_MIN_DISTANCE_PX = 36;
const CAROUSEL_SWIPE_AXIS_RATIO = 1.25;
const carouselControllerCleanups = new WeakMap<HTMLElement, () => void>();

function startCarousel(root: Root): void {
	const upNextSlide = byId(root, "paidHeroUpNext");
	const cloudSlide = byId(root, "paidHeroCloud");
	if (!upNextSlide || !cloudSlide) return;
	// Only enable rotation if the cloud has actual content (rendered keywords).
	const cloudContainer = cloudSlide.querySelector<HTMLElement>(".upnext-cloud");
	if (!cloudContainer || cloudContainer.children.length === 0) {
		setCarouselEnabled(root, false);
		return;
	}
	setCarouselEnabled(root, true);

	const slides = [upNextSlide, cloudSlide];
	const dots = Array.from(root.querySelectorAll<HTMLElement>(".upnext-dot"));
	const label = byId(root, "upnextCarouselLabel");
	const prevBtn = byId(root, "upnextPrevBtn");
	const nextBtn = byId(root, "upnextNextBtn");
	const carousel = root.querySelector<HTMLElement>(".upnext-carousel");
	if (!carousel) return;
	carouselControllerCleanups.get(carousel)?.();
	const labels = ["Up next", "Keywords"];
	// Motion preference is a user-level accessibility setting, not a visual
	// detail. Keep the carousel operable, but never advance it without an
	// explicit action when reduced motion is requested.
	const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

	let current = 0;
	let timer: ReturnType<typeof setInterval> | null = null;

	function goTo(idx: number): void {
		current = ((idx % slides.length) + slides.length) % slides.length;
		slides.forEach((s, i) => s.classList.toggle("active", i === current));
		dots.forEach((d, i) => {
			d.classList.toggle("active", i === current);
			d.setAttribute('aria-current', String(i === current));
		});
		if (label) label.textContent = labels[current] ?? "Up next";
	}

	function next(): void {
		goTo(current + 1);
	}
	function prev(): void {
		goTo(current - 1);
	}

	function restartTimer(): void {
		if (timer) clearInterval(timer);
		if (prefersReducedMotion) {
			timer = null;
			return;
		}
		timer = setInterval(next, CAROUSEL_INTERVAL_MS);
	}

	function pauseTimer(): void {
		if (timer) {
			clearInterval(timer);
			timer = null;
		}
	}

	const onPrevClick = () => {
		prev();
		restartTimer();
	};
	const onNextClick = () => {
		next();
		restartTimer();
	};
	const dotHandlers = dots.map((dot, index) => {
		const handler = () => {
			goTo(index);
			restartTimer();
		};
		dot.addEventListener('click', handler);
		return { dot, handler };
	});
	prevBtn?.addEventListener('click', onPrevClick);
	nextBtn?.addEventListener('click', onNextClick);
	const cleanupSwipe = bindCarouselSwipe(carousel, { next, prev, pauseTimer, restartTimer });
	const ownerDocument = carousel.ownerDocument;
	const onVisibilityChange = () => {
		if (ownerDocument.hidden) pauseTimer();
		else restartTimer();
	};
	ownerDocument.addEventListener('visibilitychange', onVisibilityChange);

	carouselControllerCleanups.set(carousel, () => {
		pauseTimer();
		prevBtn?.removeEventListener('click', onPrevClick);
		nextBtn?.removeEventListener('click', onNextClick);
		for (const { dot, handler } of dotHandlers) dot.removeEventListener('click', handler);
		ownerDocument.removeEventListener('visibilitychange', onVisibilityChange);
		cleanupSwipe();
	});

	goTo(0);
	restartTimer();
}

function bindCarouselSwipe(
	carousel: HTMLElement,
	controls: { next: () => void; prev: () => void; pauseTimer: () => void; restartTimer: () => void },
): () => void {
	let activePointerId: number | null = null;
	let startX = 0;
	let startY = 0;
	let suppressClickUntil = 0;
	const now = () => (typeof performance === 'undefined' ? Date.now() : performance.now());

	const onPointerDown = (event: PointerEvent) => {
		if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
		activePointerId = event.pointerId;
		startX = event.clientX;
		startY = event.clientY;
		controls.pauseTimer();
	};
	const finishPointer = (event: PointerEvent, cancelled: boolean) => {
		if (activePointerId === null || event.pointerId !== activePointerId) return;
		const deltaX = event.clientX - startX;
		const deltaY = event.clientY - startY;
		const isHorizontalSwipe =
			!cancelled && Math.abs(deltaX) >= CAROUSEL_SWIPE_MIN_DISTANCE_PX && Math.abs(deltaX) > Math.abs(deltaY) * CAROUSEL_SWIPE_AXIS_RATIO;

		activePointerId = null;
		if (isHorizontalSwipe) {
			if (deltaX < 0) controls.next();
			else controls.prev();
			suppressClickUntil = now() + 400;
		}
		controls.restartTimer();
	};
	const onPointerUp = (event: PointerEvent) => finishPointer(event, false);
	const onPointerCancel = (event: PointerEvent) => finishPointer(event, true);
	const onClickCapture = (event: MouseEvent) => {
		if (now() > suppressClickUntil) return;
		event.preventDefault();
		event.stopPropagation();
	};

	carousel.addEventListener('pointerdown', onPointerDown);
	carousel.addEventListener('pointerup', onPointerUp);
	carousel.addEventListener('pointercancel', onPointerCancel);
	carousel.addEventListener('click', onClickCapture, true);

	return () => {
		carousel.removeEventListener('pointerdown', onPointerDown);
		carousel.removeEventListener('pointerup', onPointerUp);
		carousel.removeEventListener('pointercancel', onPointerCancel);
		carousel.removeEventListener('click', onClickCapture, true);
	};
}

function setCarouselEnabled(root: Root, enabled: boolean): void {
	const carousel = root.querySelector<HTMLElement>('.upnext-carousel');
	if (!enabled && carousel) {
		carouselControllerCleanups.get(carousel)?.();
		carouselControllerCleanups.delete(carousel);
	}
	const prev = byId(root, 'upnextPrevBtn');
	const next = byId(root, 'upnextNextBtn');
	const dots = Array.from(root.querySelectorAll<HTMLElement>('.upnext-dot'));
	for (const control of [prev, next]) {
		if (control) control.hidden = !enabled;
	}
	for (const [index, dot] of dots.entries()) {
		dot.hidden = !enabled;
		dot.tabIndex = enabled ? 0 : -1;
		dot.setAttribute('role', 'button');
		dot.setAttribute('aria-label', index === 0 ? 'Show Up next' : 'Show Keywords');
		dot.setAttribute('aria-current', String(index === 0));
		dot.dataset.keyboardAction = 'true';
	}
}
